import { Trans } from '@lingui/react/macro';
import { ProjectRelativeRedirect } from '@/components/navigation';
import { checkCondition } from '@/components/permissions/helpers';
import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { hasConfig } from '@/services/booking';
import { IState } from '@/state/IState';
import { PermissionCheck, ProjectPermissions } from '@/types';
import { useAsyncState } from '@/utils/hooks';
import { MeetingsExternal } from './ExternalMeetings';
import { MeetingOverview } from './InternalMeetings/MeetingOverview';
import { MeetingTypes } from './InternalMeetings/MeetingTypes';
import { Tabs } from '@ygtechnologies/rubus-ui';

type TabId = 'external' | 'meetingtypes' | 'overview';

interface TabSetup {
    id: TabId;
    name: ReactElement;
    permissions: PermissionCheck<ProjectPermissions>;
    render: (options: { projectId: number }) => ReactElement;
}

interface MeetingsProps {
    tab: TabId;
    projectId: string | number;
}

export const Meetings: React.FC<MeetingsProps> = ({ tab, projectId }) => {
    const activeProject = useSelector((state: IState) => state.global.activeProject!);
    const [canSeeMeetings] = useAsyncState(
        async () =>
            activeProject?.permissions.isAdministrator && activeProject ? await hasConfig(activeProject?.id) : false,
        [activeProject],
        false
    );
    const tabs: TabSetup[] = [
        {
            id: 'meetingtypes',
            name: <Trans>Meeting types</Trans>,
            render: ({ projectId }) => <MeetingTypes projectId={projectId} />,
            permissions: 'isAdministrator',
        },
        {
            id: 'overview',
            name: <Trans>Meeting overview</Trans>,
            render: ({ projectId }) => <MeetingOverview projectId={projectId} />,
            permissions: 'isAdministrator',
        },
        {
            id: 'external',
            name: <Trans>BookMore</Trans>,
            render: (options) => <MeetingsExternal {...options} />,
            permissions: 'isAdministrator',
        },
    ];

    const allowedTabs = tabs.filter((x) => checkCondition(activeProject.permissions, x.permissions));

    if (!allowedTabs.find((x) => x.id === tab)) {
        if (allowedTabs.length === 0) {
            return <ProjectRelativeRedirect to="/residential-units" />;
        }

        return <ProjectRelativeRedirect to={`/meetings/${allowedTabs[0].id}`} />;
    }

    const activeTab = tabs.find((x) => x.id === tab);
    const visibleTabs = allowedTabs.filter((x) => x.id !== 'external' || canSeeMeetings);

    return (
        <Tabs.Container activeId={tab} variant="underline" sticky>
            {visibleTabs.map((t) => (
                <Tabs.Tab
                    key={t.id}
                    id={t.id}
                    as={Link}
                    to={`/projects/${projectId}/meetings/${t.id}`}
                    label={t.name}
                />
            ))}
            <Tabs.Content>{activeTab?.render({ projectId: +projectId })}</Tabs.Content>
        </Tabs.Container>
    );
};
